import { ReactElement, useEffect, useMemo } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { BreadcrumbHeading, Spinner, Tab, TabContent, TabList, TabsProvider } from '@weareopenr/component-library';
import { SpinnerContainer } from 'src/components/SpinnerContainer';
import { useInventoryItem } from 'src/hooks/useInventoryItem';
import { Routes } from 'src/routes';
import { formatDate } from 'src/helpers/formatDate/formatDate';
import { formatTime } from 'src/helpers/formatTime/formatTime';
import styles from './InventoryItem.module.scss';
import InventoryOverviewTab from 'src/components/InventoryOverviewTab';
import InventoryDietaryAttributesTab from 'src/components/InventoryDietaryAttributesTab';
import InventorySupplierDetailsTab from 'src/components/InventorySupplierDetailsTab';
import InventoryStepsTab from 'src/components/InventoryStepsTab';

interface IParams extends Record<string, string | undefined> {
  id?: string;
}

const formatTimestamp = (value: string | Date) => {
  return `${formatDate(new Date(value))}, ${formatTime(new Date(value))}`;
};

export const InventoryItem = (): ReactElement => {
  const { itemId } = useParams<IParams>();
  const { data: inventoryItem, isLoading, isFetching } = useInventoryItem(itemId);
  const isRecipe = !!inventoryItem && inventoryItem.type === 'RECIPE';

  const breadcrumbs = useMemo(
    () => [
      { label: 'Inventory', path: Routes.INVENTORY },
      { label: inventoryItem?.name, path: `${Routes.INVENTORY}/${inventoryItem?.recipe_id}` },
    ],
    [inventoryItem],
  );

  useEffect(() => {
    document.title = inventoryItem?.name || 'Inventory Item';
  }, [inventoryItem]);

  if (isLoading || (isFetching && !inventoryItem))
    return (
      <SpinnerContainer data-testid="loadingIcon">
        <Spinner size="xlarge" colour="rainbow" />
      </SpinnerContainer>
    );

  if (!itemId || !inventoryItem) return <Navigate to={Routes.INVENTORY} replace />;

  return (
    <div className={styles['page-container']}>
      <BreadcrumbHeading
        breadcrumbs={breadcrumbs}
        subtitle={`Created: ${formatTimestamp(inventoryItem.create_date)}, Last Updated: ${formatTimestamp(inventoryItem.update_date)}`}
      />
      <TabsProvider defaultTab="overview">
        <TabList className={styles['tab-list']}>
          <Tab id="overview" tabText="Overview" />
          <Tab id="nutrition" tabText="Dietary Attributes" />
          {!isRecipe && <Tab id="supplier" tabText="Supplier Details" />}
          {isRecipe && <Tab id="steps" tabText="Cooking Steps" />}
        </TabList>
        <TabContent id="overview">
          <InventoryOverviewTab inventoryItem={inventoryItem} />
        </TabContent>
        <TabContent id="nutrition">
          <InventoryDietaryAttributesTab inventoryItem={inventoryItem} />
        </TabContent>
        <TabContent id="supplier">
          <InventorySupplierDetailsTab inventoryItem={inventoryItem} />
        </TabContent>
        <TabContent id="steps">
          <InventoryStepsTab inventoryItem={inventoryItem} />
        </TabContent>
      </TabsProvider>
    </div>
  );
};
